/**
* RevisionToolGateway.js
* 
* Created on 14-mei-2014, 14:30:08
*
* @author Ivo Swartjes for Ranj
**/
import RevisionToolGatewayEvent from './RevisionToolGatewayEvent';
import LanguageRevisionInterfaceHelper from './ui/LanguageRevisionInterfaceHelper';
import { isNull } from "/lib/html5-software-library/src/ranj/util/TypeUtil";
import LanguageContextManager from './languagecontext/LanguageContextManager';
import GameContextManager from './gamecontext/GameContextManager';
import GameEvent from '../../../core/application/GameEvent';
import LanguageRevision from './LanguageRevision';
import ScenarioPlayerRegisterEvent from '../../scenario/controller/ScenarioPlayerRegisterEvent';
import DialogPlayerRegisterEvent from '../../dialog/controller/DialogPlayerRegisterEvent';

export default class RevisionToolGateway {    
    
    constructor(dispatcher, languagePack, languagePackRegistry) {
        this._eventDispatcher = dispatcher;
        this._languagePack = languagePack;
        this._languagePackRegistry = languagePackRegistry;
        this._revisableLanguagePack = languagePack;
        
        this._isEnabled = true;
        
        this._interfaceHelper;
    };
        
    setup(stageRef, applicationID, buildID, isEnabled) {
        this._interfaceHelper = new LanguageRevisionInterfaceHelper(stageRef);

        if (isNull(isEnabled)) {
            this._isEnabled = true;
        } else {
            this._isEnabled = isEnabled;
        }
        
        if (isNull(ranj.revisionAPI)) this._isEnabled = false;
        
        if (! this._isEnabled) return;
        
        this._languageContextManager = new LanguageContextManager(this, this._eventDispatcher, this._languagePack, this._languagePackRegistry);
        this._gameContextManager = new GameContextManager(this);
        
        ranj.revisionAPI.registerSWF("GameSWF", this);

        // Listen to context event for adding dialogplayers/scenarioplayers
        this._eventDispatcher.addEventListener(ScenarioPlayerRegisterEvent.REGISTER, this._handleScenarioPlayerRegister.bind(this));
        this._eventDispatcher.addEventListener(ScenarioPlayerRegisterEvent.DEREGISTER, this._handleScenarioPlayerDeregister.bind(this));
        this._eventDispatcher.addEventListener(DialogPlayerRegisterEvent.REGISTER, this._handleDialogPlayerRegister.bind(this));
        this._eventDispatcher.addEventListener(DialogPlayerRegisterEvent.DEREGISTER, this._handleDialogPlayerDeregister.bind(this));

        // Subscribe to events from revision tool
        this._subscribe(RevisionToolGatewayEvent.USE_REVISIONS);
        this._subscribe(RevisionToolGatewayEvent.REVISION_ADDED);
        this._subscribe(RevisionToolGatewayEvent.ACTIVE_REVISION);
        this._subscribe(RevisionToolGatewayEvent.GAME_PAUSED);
        
        // Ready!
        this._publish(RevisionToolGatewayEvent.READY, {applicationID: applicationID, buildID: buildID});
    };
    
    registerContentPlayer(contentPlayer) {
        if (! this._isEnabled) return;
        this._gameContextManager.registerContentPlayer(contentPlayer);
    };
    
    deregisterContentPlayer(contentPlayer) {
        if (! this._isEnabled) return;
        this._gameContextManager.deregisterContentPlayer(contentPlayer);
    };

    // ---------------- CONTEXT EVENT LISTENERS -------------

    _handleScenarioPlayerRegister({ params }) {
        const { scenarioPlayer } = params;
        this.registerContentPlayer(scenarioPlayer);
    }
    _handleScenarioPlayerDeregister({ params }) {
        const { scenarioPlayer } = params;
        this.deregisterContentPlayer(scenarioPlayer);
    }

    _handleDialogPlayerRegister({ params }) {
        const { dialogPlayer } = params;
        this.registerContentPlayer(dialogPlayer);
    }
    _handleDialogPlayerDeregister({ params }) {
        const { dialogPlayer } = params;
        this.deregisterContentPlayer(dialogPlayer);
    }

    // ---------------- PUB/SUB and retrieval of data -------------
    
    _publish(type, data) {
        if (this._isEnabled) {
            ranj.revisionAPI.publish(type, data);
        }
    };
    
    _subscribe(type) {
        if (this._isEnabled) {
            ranj.revisionAPI.subscribe(type, "receiveRTG", "GameSWF");
        }
    };
    
    _log(level, text) {
        if (this._isEnabled) {
            ranj.revisionAPI.log(level, text);
        }
    };
    
    receiveRTG(type, data) {
        console.info("Game received RTGE event: " + type + " - " + data);
        
        switch(type) {
            case RevisionToolGatewayEvent.USE_REVISIONS:
                this._setUseRevisions(data);
                break;
            case RevisionToolGatewayEvent.REVISION_ADDED:
                if (! isNull(data)) {
                    this._addRevision(new LanguageRevision(data.label, data.edited));
                }
                break;
            case RevisionToolGatewayEvent.ACTIVE_REVISION:
                if (! isNull(data)) {
                    this._setActiveRevision(new LanguageRevision(data.lpLabel, null));
                } else {
                    this._setActiveRevision(null);
                }                
                break;
            case RevisionToolGatewayEvent.GAME_PAUSED:
                this._setGamePaused(data);
                break;                                    
        }
    };
    
    // ---- OUTGOING ----
    
    setGlobalContext(context) {
        this._publish(RevisionToolGatewayEvent.CONTEXT_CHANGED, context);
    };
    
    setScenarioContext(context) {
        this._publish(RevisionToolGatewayEvent.CONTEXT_CHANGED, context);
    };
    
    setDialogContext(context) {
        this._publish(RevisionToolGatewayEvent.CONTEXT_CHANGED, context);
        
        this._interfaceHelper.clearMarkings();
        this._updateInterface();
    };
    
    addRevisableText(revisableText) {
        this._publish(RevisionToolGatewayEvent.ADDED_TO_CONTEXT, revisableText);
    };
    
    removeRevisableText(revisableText) {                
    };
    
    clearRevisableTexts() {
        this._interfaceHelper.clearMarkings();
        this._updateInterface();
        
        this._publish(RevisionToolGatewayEvent.CONTEXT_CLEARED, null);
    };
    
    // ---- INCOMING ----
    
    _setUseRevisions(useRevisions) {
        console.info("Set use revisions: " + useRevisions);
        
        this._revisableLanguagePack.useRevisions(useRevisions);
        
        this._updateInterface();
    };
    
    _addRevision(revision) {
        if (this._revisableLanguagePack.isUsingRevisions()) {
            // Showing revisions: update view
            var lastRevision = this._revisableLanguagePack.getText(revision.lpLabel);
            this._interfaceHelper.updateText(lastRevision, revision.revisedText, true);
            
        } else {
            this._revisableLanguagePack.setRevision(revision.lpLabel, revision.revisedText);
        }
        
        this._revisableLanguagePack.setRevision(revision.lpLabel, revision.revisedText);
        
        this._updateInterface();
    };
    
    _setActiveRevision(activeRevision) {
        if (isNull(activeRevision) || activeRevision.lpLabel === null) {
            console.info("Active revision: un-highlighting text.");
            this._interfaceHelper.highlightText(null);
            return;
        }
        
        console.info("Active revision: " + activeRevision.lpLabel);
        var origText = this._revisableLanguagePack.getOriginalText(activeRevision.lpLabel);
        var revisedText = this._revisableLanguagePack.getRevisedText(activeRevision.lpLabel);
        
        if (this._revisableLanguagePack.isUsingRevisions() && ! isNull(revisedText)) {
            this._interfaceHelper.highlightText(revisedText);
        } else {
            this._interfaceHelper.highlightText(origText);
        }        
    };
    
    _setGamePaused(paused) {
        if (paused !== false) {
            this._eventDispatcher.dispatchEvent(new GameEvent(GameEvent.PAUSE_GAME));
        } else {
            this._eventDispatcher.dispatchEvent(new GameEvent(GameEvent.RESUME_GAME));
        }
    };
    
    _updateInterface() {
        console.info("Updating interface. # texts in context: " + this._languageContextManager.getLPLabelsInContext().length);        
        
        for (var i = 0; i < this._languageContextManager.getLPLabelsInContext().length; i++) {
            var lpLabel = this._languageContextManager.getLPLabelsInContext()[i];
            var origText = this._revisableLanguagePack.getOriginalText(lpLabel);
            var revisedText = this._revisableLanguagePack.getRevisedText(lpLabel);
            
            if (origText !== revisedText && revisedText !== null) {
                if (this._revisableLanguagePack.isUsingRevisions()) {
                    console.info("Setting interface text '" + origText + "' to text '" + revisedText + "'");
                    this._interfaceHelper.updateText(origText, revisedText, true);
                } else {
                    console.info("Setting interface text '" + revisedText + "' to text '" + origText + "'");
                    this._interfaceHelper.updateText(revisedText, origText, true);
                }
            }
        }
    }

}